import { useState, useEffect, useMemo } from 'react'
import styled, { keyframes } from 'styled-components'
import { api } from '../../services/api'
import { Card, Input, Select, Spinner } from '../../components/shared/StyledComponents'
import { useAuth } from '../../contexts/AuthContext'
import { UserDocumentsModal } from '../../components/shared/UserDocumentsModal'
import { OpportunitySummaryModal } from '../Timeline/components/OpportunitySummaryModal'

// Modal animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const slideIn = keyframes`
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`

// Modal styled components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-out;
`

const ModalContent = styled.div`
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  width: 90%;
  max-width: 450px;
  padding: ${({ theme }) => theme.spacing.xl};
  animation: ${slideIn} 0.3s ease-out;
  text-align: center;
`

const ModalTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes.lg};
`

const ModalMessage = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.xl} 0;
  color: ${({ theme }) => theme.colors.textMuted};
`

const ModalButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  justify-content: center;
`

const ModalButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid ${({ theme, variant }) =>
        variant === 'primary' ? theme.colors.primary : theme.colors.border};
  background: ${({ theme, variant }) =>
        variant === 'primary' ? theme.colors.primary : 'white'};
  color: ${({ theme, variant }) =>
        variant === 'primary' ? 'white' : theme.colors.text};

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`

const PageContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  max-width: 100%;
`

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`

const PageTitle = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.colors.text};
`

const FiltersCard = styled(Card)`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
`

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`

const FilterLabel = styled.label`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  font-weight: 500;
`

const MatrixContainer = styled.div`
  background: ${({ theme }) => theme.colors.cardBg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow-x: auto;
  overflow-y: auto;
  padding: 0;
  max-height: 70vh;
  position: relative;
`

const MatrixTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.85rem;
`

const MatrixHeader = styled.thead`
  background: ${({ theme }) => theme.colors.primary};
  color: white;

  /* Each th handles its own sticky top so corner cells can combine top+left */
  th {
    position: sticky;
    top: 0;
    z-index: 20;
  }
`

const HeaderRow = styled.tr``

const CategoryHeader = styled.th<{ colSpan?: number }>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  text-align: center;
  font-weight: 700;
  font-size: 0.9rem;
  border-right: 2px solid white;
  background: ${({ theme }) => theme.colors.primaryDark};

  &:first-child {
    position: sticky;
    left: 0;
    top: 0;
    z-index: 30;
    background: ${({ theme }) => theme.colors.primaryDark};
    min-width: 150px;
    box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1);
  }
`

const SkillHeader = styled.th`
  padding: ${({ theme }) => theme.spacing.xs};
  text-align: center;
  font-weight: 500;
  white-space: nowrap;
  font-size: 0.7rem;
  border-right: 1px solid rgba(255, 255, 255, 0.3);
  min-width: 50px;
  max-width: 80px;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
  height: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  background: ${({ theme }) => theme.colors.primary};
`

const TableBody = styled.tbody``

const RowHeader = styled.td`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  font-weight: 600;
  white-space: nowrap;
  border-right: 2px solid ${({ theme }) => theme.colors.border};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  position: sticky;
  left: 0;
  background: white;
  z-index: 10;
  min-width: 150px;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;

  tr:nth-child(even) & {
    background: ${({ theme }) => theme.colors.background};
  }
`

const ClickableName = styled.span`
  cursor: pointer;
  color: ${({ theme }) => theme.colors.primary};
  transition: color 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.primaryDark};
    text-decoration: underline;
  }
`

interface CellProps {
    level: number
    isEditable?: boolean
}

const MatrixCell = styled.td<CellProps>`
  padding: 0;
  text-align: center;
  border: 1px solid ${({ theme }) => theme.colors.border};
  min-width: 50px;
  max-width: 50px;
  height: 36px;
  cursor: ${({ isEditable }) => (isEditable ? 'pointer' : 'default')};
  background: ${({ level }) => {
        switch (level) {
            case 0: return '#ffffff'
            case 1: return '#e8f5e9'
            case 2: return '#a5d6a7'
            case 3: return '#4caf50'
            default: return '#ffffff'
        }
    }};

  ${({ isEditable }) => isEditable && `
    &:hover {
      position: relative;
      z-index: 5;
      transform: scale(1.1);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    }
  `}
`

const CellIndicator = styled.span<CellProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 0.65rem;
  font-weight: 600;
  background: ${({ level }) => {
        switch (level) {
            case 0: return 'transparent'
            case 1: return '#c8e6c9'      // Bajo - verde claro
            case 2: return '#66bb6a'      // Medio - verde medio
            case 3: return '#2e7d32'      // Alto - verde intenso
            default: return 'transparent'
        }
    }};
  color: ${({ level }) => (level >= 2 ? 'white' : level > 0 ? '#1b5e20' : 'transparent')};
`

const Legend = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.md};
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const LegendColor = styled.div<{ color: string }>`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: ${({ color }) => color};
  border: 1px solid rgba(0, 0, 0, 0.1);
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xxl};
`

const NoData = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.textMuted};
`

const TagsFilterContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  min-height: 38px;
  background: white;
  align-items: center;
`

const FilterTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: 2px ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSizes.xs};
`

const FilterTagRemove = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0;
  font-size: 0.9rem;
  line-height: 1;
  opacity: 0.8;
  &:hover { opacity: 1; }
`

const UserOnlyMessage = styled.div`
  background: #fff3e0;
  border: 1px solid #ffcc80;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: #e65100;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`

const ColumnFilterCell = styled.th<{ hasFilter?: boolean }>`
  padding: 2px;
  text-align: center;
  background: ${({ theme, hasFilter }) => hasFilter ? theme.colors.primaryDark : theme.colors.primary};
  border-right: 1px solid rgba(255, 255, 255, 0.3);
  min-width: 50px;
  max-width: 80px;
`

const ColumnFilterSelect = styled.select<{ hasFilter?: boolean }>`
  width: 100%;
  font-size: 0.6rem;
  padding: 2px 1px;
  border: 1px solid ${({ hasFilter }) => hasFilter ? '#fff' : 'rgba(255, 255, 255, 0.3)'};
  border-radius: 3px;
  background: ${({ hasFilter }) => hasFilter ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.15)'};
  color: ${({ hasFilter }) => hasFilter ? '#333' : 'rgba(255, 255, 255, 0.7)'};
  cursor: pointer;
  appearance: none;
  text-align: center;
  text-align-last: center;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.5);
    color: #333;
  }

  &:focus {
    outline: none;
    border-color: white;
    background: rgba(255, 255, 255, 0.9);
    color: #333;
  }

  option {
    color: #333;
    background: white;
  }
`

interface SkillLevel {
    personId: string
    skillId: string
    level: number
}

interface ExperienceLevelConfig {
    min_years: number
    max_years: number
    label: string
}

interface Person {
    id: string
    name: string
    role?: string
    experience?: string
    yearsExperience?: number
    experienceLevel?: string  // Junior, Mid-Senior, Senior
    isAdmin?: boolean
    user_type?: string  // 'regular' or 'intern'
}

interface Skill {
    id: string
    name: string
    category: string
    type: 'technology' | 'sector'
}

interface Categories {
    tecnologias: Record<string, string[]>
    sectores: string[]
}

// Helper function to get experience level label
const getExperienceLevel = (years: number, levels: Record<string, ExperienceLevelConfig>): string => {
    for (const [levelName, levelDef] of Object.entries(levels)) {
        if (years >= levelDef.min_years && years <= levelDef.max_years) {
            return levelDef.label
        }
    }
    return 'Senior'  // Default for very experienced
}

export const MatrizConocimientosPage: React.FC = () => {
    const { user } = useAuth()
    const [persons, setPersons] = useState<Person[]>([])
    const [skills, setSkills] = useState<Skill[]>([])
    const [skillLevels, setSkillLevels] = useState<SkillLevel[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [categoryFilters, setCategoryFilters] = useState<string[]>([])
    const [typeFilter, setTypeFilter] = useState<'all' | 'technology' | 'sector'>('all')
    const [roleFilter, setRoleFilter] = useState('')
    const [experienceFilter, setExperienceFilter] = useState('')
    const [userTypeFilter, setUserTypeFilter] = useState<'all' | 'regular' | 'intern'>('all')
    const [columnFilters, setColumnFilters] = useState<Record<string, number>>({})

    // Modal state for admin confirmation
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [pendingChange, setPendingChange] = useState<{
        person: Person
        skill: Skill
        currentLevel: number
        newLevel: number
    } | null>(null)

    const isAdmin = user?.role === 'admin' || user?.role === 'Administrador'
    const [refreshKey, setRefreshKey] = useState(0)
    const [summaryModalOppId, setSummaryModalOppId] = useState<number | null>(null)
    // Map from skill string IDs (e.g. "tech-Backend-Java") to numeric category IDs in DB
    const [categoryIdMap, setCategoryIdMap] = useState<Record<string, number>>({})

    // Documents modal state
    const [docsModalOpen, setDocsModalOpen] = useState(false)
    const [docsModalUser, setDocsModalUser] = useState<{ id: number; name: string } | null>(null)

    // Force refresh on every mount (navigation to this page)
    useEffect(() => {
        setRefreshKey(prev => prev + 1)
    }, [])

    useEffect(() => {
        loadData()
    }, [refreshKey])

    const handleNameClick = (person: Person) => {
        setDocsModalUser({ id: Number(person.id), name: person.name })
        setDocsModalOpen(true)
    }

    const loadData = async () => {
        try {
            setLoading(true)
            const [personsRes, categoriesRes, rolesRes, matrixRes] = await Promise.all([
                api.users.getAll(),
                api.config.getCategories(),
                api.config.getRoles(),
                api.dashboard.getKnowledgeMatrix(),
            ])

            // Get experience level definitions
            const experienceLevels: Record<string, ExperienceLevelConfig> = rolesRes.data?.experience_levels || {
                Junior: { min_years: 0, max_years: 2, label: 'Junior' },
                'Mid-Senior': { min_years: 3, max_years: 5, label: 'Mid-Senior' },
                Senior: { min_years: 6, max_years: 99, label: 'Senior' },
            }
            // Roles that should NOT get experience level prefix (leadership/special roles)
            const noLevelPrefix: string[] = rolesRes.data?.no_level_prefix || []

            // Map users - filter out admin user
            const usersData = Array.isArray(personsRes.data) ? personsRes.data : []
            const mappedPersons = usersData
                .filter((u: any) => {
                    // Admin user has username 'admin' - exclude from matrix
                    const username = (u.username || '').toLowerCase()
                    return username !== 'admin'
                })
                .map((u: any) => {
                    const years = u.years_experience || 0
                    // Only show experience level if user has configured their profile
                    const hasProfile = Boolean(u.job_role) || (u.years_experience != null && u.years_experience > 0)
                    const expLevel = hasProfile ? getExperienceLevel(years, experienceLevels) : ''
                    return {
                        id: String(u.id),
                        name: u.display_name || u.username || 'Sin nombre',
                        role: u.job_role || '',
                        experience: u.years_experience ? `${u.years_experience} años` : '',
                        yearsExperience: years,
                        experienceLevel: expLevel,
                        isAdmin: false,
                        user_type: u.user_type || 'regular',
                    }
                })
            setPersons(mappedPersons)

            // Parse categories to build skills list
            const categories: Categories = categoriesRes.data || { tecnologias: {}, sectores: [] }
            const allSkills: Skill[] = []

            // Add technology skills grouped by category
            if (categories.tecnologias && typeof categories.tecnologias === 'object') {
                Object.entries(categories.tecnologias).forEach(([category, items]) => {
                    if (Array.isArray(items)) {
                        items.forEach((item: string) => {
                            allSkills.push({
                                id: `tech-${category}-${item}`,
                                name: item,
                                category,
                                type: 'technology',
                            })
                        })
                    }
                })
            }

            // Add sectors as skills
            if (Array.isArray(categories.sectores)) {
                categories.sectores.forEach((sector: string) => {
                    allSkills.push({
                        id: `sector-${sector}`,
                        name: sector,
                        category: 'Sectores',
                        type: 'sector',
                    })
                })
            }

            setSkills(allSkills)

            // Get real skill levels from the knowledge matrix API
            // The matrix API returns: { categories: [...], users: [...], knowledge: { [user_id]: { [category_id]: level_string } } }
            const matrixData = matrixRes.data || { categories: [], users: [], knowledge: {} }

            // Create a mapping from category (group + subcategory) to category_id
            const catIdMap: Record<string, number> = {}
            if (Array.isArray(matrixData.categories)) {
                matrixData.categories.forEach((cat: { id: number; category_group: string; subcategory_name: string }) => {
                    // Map both by skill name and by full key
                    const skillKey = `tech-${cat.category_group}-${cat.subcategory_name}`
                    const sectorKey = `sector-${cat.subcategory_name}`
                    catIdMap[skillKey] = cat.id
                    catIdMap[sectorKey] = cat.id
                    // Also map by just the name for fallback
                    catIdMap[cat.subcategory_name] = cat.id
                })
            }
            setCategoryIdMap(catIdMap)

            // Convert level strings to numbers: nulo=0, bajo=1, medio=2, alto=3
            const levelStringToNumber: Record<string, number> = {
                'nulo': 0,
                'bajo': 1,
                'medio': 2,
                'alto': 3,
            }

            // Build skill levels from real data
            const levels: SkillLevel[] = []
            const knowledge = matrixData.knowledge || {}

            mappedPersons.forEach((person: Person) => {
                const userKnowledge = knowledge[person.id] || {}

                allSkills.forEach((skill) => {
                    const categoryId = catIdMap[skill.id] || catIdMap[skill.name]
                    if (categoryId && userKnowledge[categoryId]) {
                        const levelStr = userKnowledge[categoryId].toLowerCase()
                        const levelNum = levelStringToNumber[levelStr] || 0
                        if (levelNum > 0) {  // Only add if not 'nulo'
                            levels.push({
                                personId: person.id,
                                skillId: skill.id,
                                level: levelNum,
                            })
                        }
                    }
                })
            })
            setSkillLevels(levels)
        } catch (error) {
            console.error('Error loading data:', error)
        } finally {
            setLoading(false)
        }
    }

    // Get unique categories for filters
    const techCategories = useMemo(() => {
        return [...new Set(skills.filter(s => s.type === 'technology').map(s => s.category))]
    }, [skills])

    const roles = useMemo(() => {
        return [...new Set(persons.map(p => p.role).filter(Boolean))]
    }, [persons])

    const experienceLevels = useMemo(() => {
        return [...new Set(persons.map(p => p.experience).filter(Boolean))]
    }, [persons])

    // Filter persons based on admin status
    const visiblePersons = useMemo(() => {
        let filtered = persons

        // Non-admin users can now see all team members (read-only)
        // They can only edit their own row

        // Apply search filter
        if (searchTerm) {
            filtered = filtered.filter(p =>
                p.name.toLowerCase().includes(searchTerm.toLowerCase())
            )
        }

        // Apply role filter
        if (roleFilter) {
            filtered = filtered.filter(p => p.role === roleFilter)
        }

        // Apply experience filter
        if (experienceFilter) {
            filtered = filtered.filter(p => p.experience === experienceFilter)
        }

        // Apply user type filter
        if (userTypeFilter !== 'all') {
            filtered = filtered.filter(p => p.user_type === userTypeFilter)
        }

        // Apply column knowledge filters
        const activeColumnFilters = Object.entries(columnFilters).filter(([, minLevel]) => minLevel > 0)
        if (activeColumnFilters.length > 0) {
            filtered = filtered.filter(person => {
                return activeColumnFilters.every(([skillId, minLevel]) => {
                    const found = skillLevels.find(
                        sl => sl.personId === person.id && sl.skillId === skillId
                    )
                    const level = found?.level || 0
                    return level >= minLevel
                })
            })
        }

        // Sort: regular users first, then interns
        return filtered.sort((a, b) => {
            const typeOrder = { regular: 0, intern: 1 }
            const aOrder = typeOrder[a.user_type as keyof typeof typeOrder] ?? 0
            const bOrder = typeOrder[b.user_type as keyof typeof typeOrder] ?? 0
            if (aOrder !== bOrder) return aOrder - bOrder
            return a.name.localeCompare(b.name)
        })
    }, [persons, isAdmin, user, searchTerm, roleFilter, experienceFilter, userTypeFilter, columnFilters, skillLevels])

    // Filter skills
    const filteredSkills = useMemo(() => {
        let filtered = skills

        if (typeFilter !== 'all') {
            filtered = filtered.filter(s => s.type === typeFilter)
        }

        if (categoryFilters.length > 0) {
            filtered = filtered.filter(s => categoryFilters.includes(s.category))
        }

        return filtered
    }, [skills, typeFilter, categoryFilters])

    // Group skills by category for grouped headers
    const groupedSkills = useMemo(() => {
        const groups: Record<string, Skill[]> = {}
        filteredSkills.forEach(skill => {
            if (!groups[skill.category]) {
                groups[skill.category] = []
            }
            groups[skill.category].push(skill)
        })
        return groups
    }, [filteredSkills])

    const getLevel = (personId: string, skillId: string): number => {
        const found = skillLevels.find(
            (sl) => sl.personId === personId && sl.skillId === skillId
        )
        return found?.level || 0
    }

    // Apply the skill level change
    const applySkillChange = async (person: Person, skill: Skill, newLevel: number) => {
        const levelLabels = ['nulo', 'bajo', 'medio', 'alto']
        const currentLevel = skillLevels.find(
            sl => sl.personId === person.id && sl.skillId === skill.id
        )?.level || 0

        // Optimistically update UI
        setSkillLevels(prev => {
            const existing = prev.find(
                sl => sl.personId === person.id && sl.skillId === skill.id
            )
            if (existing) {
                if (newLevel === 0) {
                    return prev.filter(sl => sl !== existing)
                }
                return prev.map(sl =>
                    sl === existing ? { ...sl, level: newLevel } : sl
                )
            }
            if (newLevel > 0) {
                return [...prev, { personId: person.id, skillId: skill.id, level: newLevel }]
            }
            return prev
        })

        // Resolve the numeric category ID from the skill string ID
        const numericCategoryId = categoryIdMap[skill.id] || categoryIdMap[skill.name]
        if (!numericCategoryId) {
            console.error('Could not resolve category ID for skill:', skill.id, skill.name)
            // Revert on error
            setSkillLevels(prev => prev.map(sl =>
                sl.personId === person.id && sl.skillId === skill.id
                    ? { ...sl, level: currentLevel }
                    : sl
            ))
            alert('Error: No se pudo identificar la categoría. Por favor, recarga la página.')
            return
        }

        // Save to API - use admin endpoint for other users, regular endpoint for own edits
        try {
            const isOwnEdit = String(person.id) === String(user?.id)
            if (isOwnEdit) {
                await api.dashboard.updateCategoryKnowledge(numericCategoryId, levelLabels[newLevel])
            } else {
                await api.dashboard.updateUserKnowledge(
                    Number(person.id),
                    numericCategoryId,
                    levelLabels[newLevel]
                )
            }
        } catch (error) {
            console.error('Error updating knowledge:', error)
            // Revert on error
            setSkillLevels(prev => {
                const existing = prev.find(
                    sl => sl.personId === person.id && sl.skillId === skill.id
                )
                if (existing) {
                    return prev.map(sl =>
                        sl.personId === person.id && sl.skillId === skill.id
                            ? { ...sl, level: currentLevel }
                            : sl
                    )
                }
                return prev
            })
            alert('Error al guardar los cambios. Por favor, inténtelo de nuevo.')
        }
    }

    const handleCellClick = (person: Person, skill: Skill, currentLevel: number) => {
        // Only allow editing own skills if not admin
        if (!isAdmin && String(person.id) !== String(user?.id)) {
            return
        }

        // Cycle through 4 levels: 0 (nulo) -> 1 (bajo) -> 2 (medio) -> 3 (alto) -> 0
        const newLevel = (currentLevel + 1) % 4

        // If admin is editing another user's skill, show confirmation modal
        if (isAdmin && String(person.id) !== String(user?.id)) {
            setPendingChange({ person, skill, currentLevel, newLevel })
            setShowConfirmModal(true)
            return
        }

        // For own edits, apply directly
        applySkillChange(person, skill, newLevel)
    }

    const handleConfirmChange = () => {
        if (pendingChange) {
            applySkillChange(pendingChange.person, pendingChange.skill, pendingChange.newLevel)
        }
        setShowConfirmModal(false)
        setPendingChange(null)
    }

    const handleCancelChange = () => {
        setShowConfirmModal(false)
        setPendingChange(null)
    }

    const canEdit = (personId: string) => {
        return isAdmin || String(personId) === String(user?.id)
    }

    if (loading) {
        return (
            <PageContainer>
                <PageTitle>📚 Matriz de Conocimientos</PageTitle>
                <LoadingContainer>
                    <Spinner />
                </LoadingContainer>
            </PageContainer>
        )
    }

    const categoryKeys = Object.keys(groupedSkills)

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>📚 Matriz de Conocimientos</PageTitle>
            </PageHeader>

            {!isAdmin && (
                <UserOnlyMessage>
                    ⚠️ Puedes ver la matriz completa del equipo, pero solo puedes editar tus propias competencias. Haz clic en tus celdas para actualizar tu nivel.
                </UserOnlyMessage>
            )}

            <FiltersCard>
                <FiltersGrid>
                    <FilterGroup>
                        <FilterLabel>Buscar persona</FilterLabel>
                        <Input
                            type="text"
                            placeholder="Nombre..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </FilterGroup>
                    {roles.length > 0 && (
                        <FilterGroup>
                            <FilterLabel>Rol</FilterLabel>
                            <Select
                                value={roleFilter}
                                onChange={(e) => setRoleFilter(e.target.value)}
                            >
                                <option value="">Todos</option>
                                {roles.map((role) => (
                                    <option key={role} value={role}>
                                        {role}
                                    </option>
                                ))}
                            </Select>
                        </FilterGroup>
                    )}
                    {experienceLevels.length > 0 && (
                        <FilterGroup>
                            <FilterLabel>Experiencia</FilterLabel>
                            <Select
                                value={experienceFilter}
                                onChange={(e) => setExperienceFilter(e.target.value)}
                            >
                                <option value="">Todos</option>
                                {experienceLevels.map((exp) => (
                                    <option key={exp} value={exp}>
                                        {exp}
                                    </option>
                                ))}
                            </Select>
                        </FilterGroup>
                    )}
                    <FilterGroup>
                        <FilterLabel>Tipo Usuario</FilterLabel>
                        <Select
                            value={userTypeFilter}
                            onChange={(e) => setUserTypeFilter(e.target.value as any)}
                        >
                            <option value="all">Todos</option>
                            <option value="regular">Empleados</option>
                            <option value="intern">Prácticas/Formación</option>
                        </Select>
                    </FilterGroup>
                    <FilterGroup>
                        <FilterLabel>Tipo</FilterLabel>
                        <Select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value as any)}
                        >
                            <option value="all">Todo</option>
                            <option value="technology">Tecnologías</option>
                            <option value="sector">Sectores</option>
                        </Select>
                    </FilterGroup>
                    <FilterGroup style={{ gridColumn: 'span 2' }}>
                        <FilterLabel>Categorías</FilterLabel>
                        <TagsFilterContainer>
                            {categoryFilters.map((cat) => (
                                <FilterTag key={cat}>
                                    {cat}
                                    <FilterTagRemove onClick={() => setCategoryFilters(prev => prev.filter(c => c !== cat))}>×</FilterTagRemove>
                                </FilterTag>
                            ))}
                            <Select
                                style={{ flex: 1, minWidth: 140, border: 'none', padding: '2px 4px' }}
                                value=""
                                onChange={(e) => {
                                    if (e.target.value && !categoryFilters.includes(e.target.value)) {
                                        setCategoryFilters(prev => [...prev, e.target.value])
                                    }
                                }}
                            >
                                <option value="">{categoryFilters.length === 0 ? 'Todas las categorías' : '+ Añadir categoría'}</option>
                                {techCategories.filter(cat => !categoryFilters.includes(cat)).map((cat) => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                                {!categoryFilters.includes('Sectores') && (
                                    <option value="Sectores">Sectores</option>
                                )}
                            </Select>
                        </TagsFilterContainer>
                    </FilterGroup>
                </FiltersGrid>
                {(searchTerm || roleFilter || experienceFilter || userTypeFilter !== 'all' || typeFilter !== 'all' || categoryFilters.length > 0 || Object.keys(columnFilters).length > 0) && (
                    <div style={{ marginTop: '12px', textAlign: 'right' }}>
                        <button
                            onClick={() => {
                                setSearchTerm('')
                                setRoleFilter('')
                                setExperienceFilter('')
                                setUserTypeFilter('all')
                                setTypeFilter('all')
                                setCategoryFilters([])
                                setColumnFilters({})
                            }}
                            style={{
                                background: 'none',
                                border: '1px solid #ccc',
                                borderRadius: '6px',
                                padding: '6px 16px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                color: '#666',
                                transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#f44336'
                                e.currentTarget.style.color = '#f44336'
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = '#ccc'
                                e.currentTarget.style.color = '#666'
                            }}
                        >
                            🗑️ Limpiar todos los filtros
                        </button>
                    </div>
                )}
            </FiltersCard>

            {visiblePersons.length === 0 || filteredSkills.length === 0 ? (
                <NoData>
                    <p>No hay datos para mostrar con los filtros actuales</p>
                </NoData>
            ) : (
                <>
                    <MatrixContainer>
                        <MatrixTable>
                            <MatrixHeader>
                                {/* Category headers row */}
                                <HeaderRow>
                                    <CategoryHeader rowSpan={3}>Persona</CategoryHeader>
                                    {categoryKeys.map((category) => (
                                        <CategoryHeader
                                            key={category}
                                            colSpan={groupedSkills[category].length}
                                        >
                                            {category}
                                        </CategoryHeader>
                                    ))}
                                </HeaderRow>
                                {/* Skill headers row */}
                                <HeaderRow>
                                    {categoryKeys.map((category) =>
                                        groupedSkills[category].map((skill) => (
                                            <SkillHeader
                                                key={skill.id}
                                                title={`${skill.name} (${skill.category})`}
                                            >
                                                {skill.name}
                                            </SkillHeader>
                                        ))
                                    )}
                                </HeaderRow>
                                {/* Column filter row */}
                                <HeaderRow>
                                    {categoryKeys.map((category) =>
                                        groupedSkills[category].map((skill) => {
                                            const currentFilter = columnFilters[skill.id] || 0
                                            return (
                                                <ColumnFilterCell key={`filter-${skill.id}`} hasFilter={currentFilter > 0}>
                                                    <ColumnFilterSelect
                                                        hasFilter={currentFilter > 0}
                                                        value={currentFilter}
                                                        onChange={(e) => {
                                                            const val = Number(e.target.value)
                                                            setColumnFilters(prev => {
                                                                const next = { ...prev }
                                                                if (val === 0) {
                                                                    delete next[skill.id]
                                                                } else {
                                                                    next[skill.id] = val
                                                                }
                                                                return next
                                                            })
                                                        }}
                                                        title={`Filtrar ${skill.name}: nivel mínimo`}
                                                    >
                                                        <option value={0}>-</option>
                                                        <option value={1}>≥B</option>
                                                        <option value={2}>≥M</option>
                                                        <option value={3}>A</option>
                                                    </ColumnFilterSelect>
                                                </ColumnFilterCell>
                                            )
                                        })
                                    )}
                                </HeaderRow>
                            </MatrixHeader>
                            <TableBody>
                                {visiblePersons.map((person) => (
                                    <tr key={person.id}>
                                        <RowHeader>
                                            <ClickableName
                                                onClick={() => handleNameClick(person)}
                                                title="Ver CV y certificados"
                                            >
                                                {person.name}
                                            </ClickableName>
                                            {isAdmin && (person.experienceLevel || person.role) && (
                                                <div style={{ fontSize: '0.7rem', color: '#666', fontWeight: 'normal' }}>
                                                    {person.role || person.experienceLevel}
                                                </div>
                                            )}
                                        </RowHeader>
                                        {categoryKeys.map((category) =>
                                            groupedSkills[category].map((skill) => {
                                                const level = getLevel(person.id, skill.id)
                                                const editable = canEdit(person.id)
                                                const levelLabels = ['', 'B', 'M', 'A']  // Bajo, Medio, Alto
                                                const levelNames = ['Nulo', 'Bajo', 'Medio', 'Alto']
                                                return (
                                                    <MatrixCell
                                                        key={skill.id}
                                                        level={level}
                                                        isEditable={editable}
                                                        onClick={() => editable && handleCellClick(person, skill, level)}
                                                        title={`${person.name} - ${skill.name}: ${levelNames[level]}${editable ? ' (clic para cambiar)' : ''}`}
                                                    >
                                                        {level > 0 && (
                                                            <CellIndicator level={level}>{levelLabels[level]}</CellIndicator>
                                                        )}
                                                    </MatrixCell>
                                                )
                                            })
                                        )}
                                    </tr>
                                ))}
                            </TableBody>
                        </MatrixTable>
                    </MatrixContainer>

                    <Legend>
                        <LegendItem>
                            <LegendColor color="#ffffff" />
                            <span>NA - Sin conocimiento</span>
                        </LegendItem>
                        <LegendItem>
                            <LegendColor color="#c8e6c9" />
                            <span>Bajo</span>
                        </LegendItem>
                        <LegendItem>
                            <LegendColor color="#66bb6a" />
                            <span>Medio</span>
                        </LegendItem>
                        <LegendItem>
                            <LegendColor color="#2e7d32" />
                            <span>Alto</span>
                        </LegendItem>
                    </Legend>
                </>
            )}

            {/* Confirmation Modal for Admin Edits */}
            {showConfirmModal && pendingChange && (
                <ModalOverlay onClick={handleCancelChange}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <ModalTitle>⚠️ Confirmar cambio</ModalTitle>
                        <ModalMessage>
                            ¿Estás seguro de que quieres cambiar el nivel de conocimiento de{' '}
                            <strong>{pendingChange.person.name}</strong> en{' '}
                            <strong>{pendingChange.skill.name}</strong> de{' '}
                            <strong>{['Nulo', 'Bajo', 'Medio', 'Alto'][pendingChange.currentLevel]}</strong> a{' '}
                            <strong>{['Nulo', 'Bajo', 'Medio', 'Alto'][pendingChange.newLevel]}</strong>?
                        </ModalMessage>
                        <ModalButtons>
                            <ModalButton variant="secondary" onClick={handleCancelChange}>
                                Cancelar
                            </ModalButton>
                            <ModalButton variant="primary" onClick={handleConfirmChange}>
                                Aceptar cambios
                            </ModalButton>
                        </ModalButtons>
                    </ModalContent>
                </ModalOverlay>
            )}

            {docsModalUser && (
                <UserDocumentsModal
                    isOpen={docsModalOpen}
                    onClose={() => { setDocsModalOpen(false); setDocsModalUser(null) }}
                    userId={docsModalUser.id}
                    userName={docsModalUser.name}
                    isAdmin={isAdmin}
                />
            )}

            {summaryModalOppId && (
                <OpportunitySummaryModal
                    opportunityId={summaryModalOppId}
                    onClose={() => setSummaryModalOppId(null)}
                    isAdmin={isAdmin}
                />
            )}

        </PageContainer>
    )
}
